import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ExternalLink,
  Check,
  AlertTriangle,
  Key,
  Phone,
  Mic,
  Brain,
  Loader2,
  CheckCircle,
  XCircle,
  ChevronDown,
  HelpCircle,
  Zap,
  Settings,
  Globe,
  Eye,
  EyeOff,
  Trash2,
  PlayCircle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { VapiAccountManager } from '@/components/VapiAccountManager';
import { TrainingVideoModal } from '@/components/TrainingVideoModal';
import { useLocationTrainingVideo } from '@/hooks/useLocationTrainingVideo';
import { ApiKeyCard } from '@/components/ApiKeyCard';

const ApiSettings = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'ai-providers';
  const { apiKeys: authApiKeys, refreshApiKeys, user } = useAuth();
  const { hasVideo, displayTitle } = useLocationTrainingVideo('api-settings');
  const [isLoading, setIsLoading] = useState(false);
  const [webhookProvider, setWebhookProvider] = useState<
    'zapier' | 'make' | 'none'
  >('none');
  const [isSwitching, setIsSwitching] = useState(false);
  const [showSecrets, setShowSecrets] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [platformApiMode, setPlatformApiMode] = useState(false);

  const [formData, setFormData] = useState({
    // AI Providers
    openaiApiKey: '',
    xaiApiKey: '',

    // Communication Providers (only Twilio now)
    twilioSid: '',
    twilioAuthToken: '',

    // Voice Providers
    elevenlabsApiKey: '',
    vapiApiKey: '',

    // Webhooks & Automation
    webhookUrl: '', // Single unified webhook field
    zapierWebhook: '', // Keep for legacy support
    makeWebhook: '', // Keep for legacy support
  });

  // Individual validation states for each service
  const [validationStatus, setValidationStatus] = useState<{
    [key: string]: {
      status: 'idle' | 'saving' | 'valid' | 'invalid';
      message?: string;
    };
  }>({
    openai: { status: 'idle' },
    xai: { status: 'idle' },
    twilio: { status: 'idle' },
    elevenlabs: { status: 'idle' },
    vapi: { status: 'idle' },
    zapier: { status: 'idle' },
    make: { status: 'idle' },
    webhook: { status: 'idle' },
  });

  // draftKey & serviceFields added to persist form fields when navigating away from tab
  const draftKey = 'api_settings_drafts';

  const serviceFields: { [key: string]: string[] } = {
    openai: ['openaiApiKey'],
    xai: ['xaiApiKey'],
    twilio: ['twilioSid', 'twilioAuthToken'],
    elevenlabs: ['elevenlabsApiKey'],
    vapi: ['vapiApiKey'],
    zapier: ['zapierWebhook'],
    make: ['makeWebhook'],
    webhook: ['webhookUrl'],
  };

  // Fetch platform API mode setting (public setting, no auth required)
  useEffect(() => {
    supabase
      .from('system_settings')
      .select('setting_value')
      .eq('setting_key', 'platform_api_mode')
      .single()
      .then(({ data }) => {
        if (data) setPlatformApiMode(data.setting_value === 'true');
      });
  }, []);

  // FIXED: Fetch fresh data on mount without dependency loop
  useEffect(() => {
    if (user?.id) {
      // console.log(
      //   `[ApiSettings] [${new Date().toISOString()}] Component mounted, fetching API keys`
      // );
      refreshApiKeys();
    }
  }, [user?.id]); // ONLY depend on user?.id to prevent infinite loop

  // Sync form fields with AuthContext API keys
  useEffect(() => {
    if (authApiKeys) {
      // console.log("Syncing API Settings with AuthContext:", authApiKeys);

      setFormData((prev) => ({
        ...prev,
        openaiApiKey: authApiKeys.openai || '',
        xaiApiKey: authApiKeys.grok || authApiKeys.xai || '',
        twilioSid: authApiKeys.twilioSid || '',
        twilioAuthToken: authApiKeys.twilioAuthToken || '',
        elevenlabsApiKey: authApiKeys.elevenlabs || '',
        vapiApiKey: authApiKeys.vapi || '',
        webhookUrl: authApiKeys.webhookUrl || '', // Single unified webhook field
        zapierWebhook: authApiKeys.zapierWebhook || '', // Keep for legacy support
        makeWebhook: authApiKeys.makeWebhook || '', // Keep for legacy support
      }));

      // Update validation status based on AuthContext keys
      setValidationStatus((prev) => ({
        ...prev,
        openai: {
          status: authApiKeys.openai ? 'valid' : 'idle',
          message: authApiKeys.openai ? 'API key configured' : undefined,
        },
        xai: {
          status: authApiKeys.grok || authApiKeys.xai ? 'valid' : 'idle',
          message:
            authApiKeys.grok || authApiKeys.xai
              ? 'API key configured'
              : undefined,
        },
        twilio: {
          status:
            authApiKeys.twilioSid && authApiKeys.twilioAuthToken
              ? 'valid'
              : 'idle',
          message:
            authApiKeys.twilioSid && authApiKeys.twilioAuthToken
              ? 'Twilio configured'
              : undefined,
        },
        elevenlabs: {
          status: authApiKeys.elevenlabs ? 'valid' : 'idle',
          message: authApiKeys.elevenlabs ? 'API key configured' : undefined,
        },
        vapi: {
          status: authApiKeys.vapi ? 'valid' : 'idle',
          message: authApiKeys.vapi ? 'API key configured' : undefined,
        },
        zapier: {
          status: authApiKeys.zapierWebhook ? 'valid' : 'idle',
          message: authApiKeys.zapierWebhook ? 'Webhook configured' : undefined,
        },
        make: {
          status: authApiKeys.makeWebhook ? 'valid' : 'idle',
          message: authApiKeys.makeWebhook ? 'Webhook configured' : undefined,
        },
        webhook: {
          status: authApiKeys.webhookUrl ? 'valid' : 'idle',
          message: authApiKeys.webhookUrl ? 'Webhook configured' : undefined,
        },
      }));

      // Load and apply drafts (overrides saved/masked values with unsaved entries)
      const drafts = JSON.parse(sessionStorage.getItem(draftKey) || '{}');
      setFormData((prev) => ({
        ...prev,
        ...drafts,
      }));
    }
  }, [authApiKeys]);

  // Determine active webhook provider based on API keys
  useEffect(() => {
    if (authApiKeys && !isSwitching) {
      // Default based on existing webhook URLs, but radio selection will be authoritative
      if (authApiKeys.zapierWebhook && !authApiKeys.makeWebhook) {
        setWebhookProvider('zapier');
      } else if (authApiKeys.makeWebhook && !authApiKeys.zapierWebhook) {
        setWebhookProvider('make');
      } else if (!authApiKeys.zapierWebhook && !authApiKeys.makeWebhook) {
        setWebhookProvider('none');
      }
      // If both exist, keep current selection or default to none
    }
  }, [authApiKeys, isSwitching]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    const drafts = JSON.parse(sessionStorage.getItem(draftKey) || '{}');
    if (value.trim() === '') {
      delete drafts[field];
    } else {
      drafts[field] = value;
    }
    if (Object.keys(drafts).length === 0) {
      sessionStorage.removeItem(draftKey);
    } else {
      sessionStorage.setItem(draftKey, JSON.stringify(drafts));
    }
  };

  // Enhanced save function with targeted cache clearing
  const saveApiKey = async (
    provider: string,
    keyName: string,
    value: string
  ): Promise<{
    success: boolean;
    maskedSuffix?: string;
    updatedAt?: string;
  }> => {
    try {
      // TARGETED CACHE CLEAR - Only clear API keys cache, preserve profile cache
      if (user?.id) {
        const cacheKey = `auth_api_keys_cache_${user.id}`;
        localStorage.removeItem(cacheKey);
        // console.log(`[ApiSettings] [${new Date().toISOString()}] Cleared API keys cache before save:`, cacheKey);
      }

      const { data, error } = await supabase.functions.invoke(
        'manage-api-keys',
        {
          body: {
            action: 'save',
            provider,
            key_name: keyName,
            value,
          },
        }
      );

      if (error) throw new Error(error.message || 'Failed to save API key');

      // console.log(`[ApiSettings] [${new Date().toISOString()}] Successfully saved ${provider} key`, {
      //   maskedSuffix: data?.maskedSuffix,
      //   updatedAt: data?.updatedAt
      // });
      return data || { success: true };
    } catch (error) {
      console.error(
        `[ApiSettings] [${new Date().toISOString()}] Error saving ${provider} key:`,
        error
      );
      return { success: false };
    }
  };

  const handleSaveService = async (serviceKey: string) => {
    // console.log(`[ApiSettings] [${new Date().toISOString()}] Saving ${serviceKey}...`);

    // OPTIMISTIC UI UPDATE - Show saving immediately
    setValidationStatus((prev) => ({
      ...prev,
      [serviceKey]: { status: 'saving', message: 'Saving configuration...' },
    }));

    try {
      let success = false;
      let serviceName = '';
      let saveResult: { success: boolean; maskedSuffix?: string } = {
        success: false,
      };

      // Validate Twilio requires both keys
      if (serviceKey === 'twilio') {
        if (!formData.twilioSid || !formData.twilioAuthToken) {
          setValidationStatus((prev) => ({
            ...prev,
            [serviceKey]: {
              status: 'invalid',
              message: 'Both Twilio SID and Auth Token are required',
            },
          }));
          toast({
            title: 'Validation Error',
            description: 'Both Twilio SID and Auth Token are required',
            variant: 'destructive',
          });
          return;
        }
      }

      switch (serviceKey) {
        case 'openai':
          if (formData.openaiApiKey) {
            saveResult = await saveApiKey(
              'openai',
              'api_key',
              formData.openaiApiKey
            );
            success = saveResult.success;
            serviceName = 'OpenAI';
          }
          break;

        case 'xai':
          if (formData.xaiApiKey) {
            saveResult = await saveApiKey(
              'grok',
              'api_key',
              formData.xaiApiKey
            );
            success = saveResult.success;
            serviceName = 'xAI (Grok)';
          }
          break;

        case 'twilio':
          if (formData.twilioSid && formData.twilioAuthToken) {
            const sidResult = await saveApiKey(
              'twilio',
              'account_sid',
              formData.twilioSid
            );
            const tokenResult = await saveApiKey(
              'twilio',
              'auth_token',
              formData.twilioAuthToken
            );
            success = sidResult.success && tokenResult.success;
            saveResult = tokenResult; // Use token result for display
            serviceName = 'Twilio';
          }
          break;

        case 'elevenlabs':
          if (formData.elevenlabsApiKey) {
            saveResult = await saveApiKey(
              'elevenlabs',
              'api_key',
              formData.elevenlabsApiKey
            );
            success = saveResult.success;
            serviceName = 'ElevenLabs';
          }
          break;

        case 'vapi':
          if (formData.vapiApiKey) {
            saveResult = await saveApiKey(
              'vapi',
              'api_key',
              formData.vapiApiKey
            );
            success = saveResult.success;
            serviceName = 'Vapi';
          }
          break;

        case 'zapier':
          if (formData.zapierWebhook) {
            saveResult = await saveApiKey(
              'zapier',
              'webhook_url',
              formData.zapierWebhook
            );
            success = saveResult.success;
            serviceName = 'Zapier';
          }
          break;

        case 'make':
          if (formData.makeWebhook) {
            saveResult = await saveApiKey(
              'make',
              'webhook_url',
              formData.makeWebhook
            );
            success = saveResult.success;
            serviceName = 'Make.com';
          }
          break;
      }

      if (!success) {
        throw new Error(`Failed to save ${serviceName} configuration`);
      }

      // Clear relevant drafts after successful save
      const drafts = JSON.parse(sessionStorage.getItem(draftKey) || '{}');
      serviceFields[serviceKey]?.forEach((field) => delete drafts[field]);
      if (Object.keys(drafts).length === 0) {
        sessionStorage.removeItem(draftKey);
      } else {
        sessionStorage.setItem(draftKey, JSON.stringify(drafts));
      }

      // OPTIMISTIC UI UPDATE - Show success immediately
      setValidationStatus((prev) => ({
        ...prev,
        [serviceKey]: {
          status: 'valid',
          message: `${serviceName} configured successfully (suffix: ${
            saveResult.maskedSuffix || 'unknown'
          })`,
        },
      }));

      // Show success toast immediately
      toast({
        title: 'Configuration Saved',
        description: `Your ${serviceName} settings have been saved successfully.`,
      });

      // TARGETED CACHE REFRESH - Only refresh API keys, preserve profile
      // console.log(`[ApiSettings] [${new Date().toISOString()}] Refreshing API keys for ${serviceName}`);

      // Force refresh API keys only
      await refreshApiKeys();
    } catch (error) {
      console.error(
        `[ApiSettings] [${new Date().toISOString()}] Save failed for ${serviceKey}:`,
        error
      );

      setValidationStatus((prev) => ({
        ...prev,
        [serviceKey]: {
          status: 'invalid',
          message: `Failed to save: ${error.message || 'Unknown error'}`,
        },
      }));

      toast({
        title: 'Save Failed',
        description: `Failed to save ${serviceKey} configuration. Please try again.`,
        variant: 'destructive',
      });
    }
  };

  const handleWebhookProviderChange = async (
    provider: 'zapier' | 'make' | 'none'
  ) => {
    const previousProvider = webhookProvider;
    const previousValidationStatus = { ...validationStatus };

    try {
      // Set switching flag to prevent authApiKeys sync conflicts
      setIsSwitching(true);

      // Optimistic UI updates - immediately set provider and update validation statuses
      setWebhookProvider(provider);

      // Update validation status immediately based on selection
      const newValidationStatus = { ...validationStatus };

      if (provider === 'none') {
        newValidationStatus.zapier = { status: 'idle', message: undefined };
        newValidationStatus.make = { status: 'idle', message: undefined };
      } else if (provider === 'zapier') {
        newValidationStatus.zapier = formData.zapierWebhook
          ? { status: 'valid', message: 'Active webhook provider' }
          : { status: 'idle', message: 'Webhook URL required' };
        newValidationStatus.make = { status: 'idle', message: undefined };
      } else if (provider === 'make') {
        newValidationStatus.make = formData.makeWebhook
          ? { status: 'valid', message: 'Active webhook provider' }
          : { status: 'idle', message: 'Webhook URL required' };
        newValidationStatus.zapier = { status: 'idle', message: undefined };
      }

      setValidationStatus(newValidationStatus);

      const { data, error } = await supabase.functions.invoke(
        'manage-api-keys',
        {
          body: {
            action: 'set_webhook_provider',
            provider,
          },
        }
      );

      if (error)
        throw new Error(error.message || 'Failed to set webhook provider');

      // Use activeProviders from response to confirm server state
      const serverActiveProviders = data?.activeProviders || [];
      const serverProvider = serverActiveProviders.includes('zapier')
        ? 'zapier'
        : serverActiveProviders.includes('make')
        ? 'make'
        : 'none';

      // Reconcile if server state differs from our optimistic update
      if (serverProvider !== provider) {
        // console.warn(`Server state mismatch: expected ${provider}, got ${serverProvider}`);
        setWebhookProvider(serverProvider);
      }

      toast({
        title: 'Webhook Provider Updated',
        description: `Active webhook provider set to: ${
          provider === 'none'
            ? 'None'
            : provider === 'zapier'
            ? 'Zapier'
            : 'Make.com'
        }`,
      });

      // Refresh API keys to sync with server
      await refreshApiKeys();
    } catch (error) {
      // Revert all optimistic updates on error
      setWebhookProvider(previousProvider);
      setValidationStatus(previousValidationStatus);
      console.error('Error setting webhook provider:', error);
      toast({
        title: 'Failed to Update',
        description: `Failed to set webhook provider. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setIsSwitching(false);
    }
  };

  const handleSaveWebhook = async () => {
    // console.log(`[ApiSettings] [${new Date().toISOString()}] Saving webhook...`);

    // OPTIMISTIC UI UPDATE - Show saving immediately
    setValidationStatus((prev) => ({
      ...prev,
      webhook: { status: 'saving', message: 'Saving webhook...' },
    }));

    try {
      if (!formData.webhookUrl) {
        setValidationStatus((prev) => ({
          ...prev,
          webhook: { status: 'invalid', message: 'Webhook URL is required' },
        }));
        toast({
          title: 'Validation Error',
          description: 'Webhook URL is required',
          variant: 'destructive',
        });
        return;
      }

      const saveResult = await saveApiKey(
        'webhook',
        'url',
        formData.webhookUrl
      );

      if (!saveResult.success) {
        throw new Error('Failed to save webhook URL');
      }

      // Clear relevant drafts after successful save
      const drafts = JSON.parse(sessionStorage.getItem(draftKey) || '{}');
      serviceFields['webhook']?.forEach((field) => delete drafts[field]);
      if (Object.keys(drafts).length === 0) {
        sessionStorage.removeItem(draftKey);
      } else {
        sessionStorage.setItem(draftKey, JSON.stringify(drafts));
      }

      // OPTIMISTIC UI UPDATE - Show success immediately
      setValidationStatus((prev) => ({
        ...prev,
        webhook: {
          status: 'valid',
          message: `Webhook configured successfully (suffix: ${
            saveResult.maskedSuffix || 'unknown'
          })`,
        },
      }));

      // Show success toast immediately
      toast({
        title: 'Webhook Saved',
        description: 'Your webhook URL has been saved successfully.',
      });

      await refreshApiKeys();
    } catch (error) {
      console.error(
        `[ApiSettings] [${new Date().toISOString()}] Webhook save failed:`,
        error
      );

      setValidationStatus((prev) => ({
        ...prev,
        webhook: {
          status: 'invalid',
          message: `Failed to save: ${error.message || 'Unknown error'}`,
        },
      }));

      toast({
        title: 'Save Failed',
        description: 'Failed to save webhook configuration. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleClearWebhook = async () => {
    try {
      if (user?.id) {
        const cacheKey = `auth_api_keys_cache_${user.id}`;
        localStorage.removeItem(cacheKey);
      }

      const { data, error } = await supabase.functions.invoke(
        'manage-api-keys',
        {
          body: {
            action: 'clear_webhook_url',
            // No provider specified - clears all webhook entries
          },
        }
      );

      if (error)
        throw new Error(error.message || 'Failed to clear webhook URL');

      // Check if the backend operation actually succeeded
      if (!data?.success) {
        throw new Error(data?.error || 'No active webhook found to clear');
      }

      // Clear the form field only after successful backend operation
      setFormData((prev) => ({
        ...prev,
        webhookUrl: '',
      }));

      // Clear relevant drafts after success
      const drafts = JSON.parse(sessionStorage.getItem(draftKey) || '{}');
      serviceFields['webhook']?.forEach((field) => delete drafts[field]);
      if (Object.keys(drafts).length === 0) {
        sessionStorage.removeItem(draftKey);
      } else {
        sessionStorage.setItem(draftKey, JSON.stringify(drafts));
      }

      // Update validation status to reflect clearing
      setValidationStatus((prev) => ({
        ...prev,
        webhook: { status: 'idle', message: undefined },
      }));

      toast({
        title: 'Webhook URL Cleared',
        description: 'Webhook URL has been removed.',
      });

      // Refresh API keys to sync with server
      await refreshApiKeys();
    } catch (error) {
      console.error(`Error clearing webhook:`, error);
      toast({
        title: 'Clear Failed',
        description: `${
          error.message || 'Failed to clear webhook URL. Please try again.'
        }`,
        variant: 'destructive',
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'saving':
        return <Loader2 className='h-4 w-4 animate-spin text-blue-500' />;
      case 'valid':
        return <CheckCircle className='h-4 w-4 text-green-500' />;
      case 'invalid':
        return <XCircle className='h-4 w-4 text-red-500' />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string, required: boolean = false) => {
    if (status === 'valid') {
      return (
        <Badge
          variant='default'
          className='text-xs bg-green-100 text-green-800'
        >
          Configured
        </Badge>
      );
    }
    if (status === 'invalid') {
      return (
        <Badge variant='destructive' className='text-xs'>
          Invalid
        </Badge>
      );
    }
    return required ? (
      <Badge variant='destructive' className='text-xs'>
        Required
      </Badge>
    ) : (
      <Badge variant='secondary' className='text-xs'>
        Optional
      </Badge>
    );
  };

  const toggleSecretVisibility = (fieldKey: string) => {
    setShowSecrets((prev) => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  };

  return (
    <TooltipProvider>
      <div className='container mx-auto p-6 max-w-6xl'>
        <div className='mb-6'>
          <h1 className='text-3xl font-bold'>API Settings</h1>
          <p className='text-muted-foreground'>
            Configure your API keys and integrations for campaigns, scripts, and
            automations.
          </p>
        </div>

        {platformApiMode && (
          <Alert className='mb-6 border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-950'>
            <Key className='h-4 w-4 text-blue-600' />
            <AlertDescription className='text-blue-800 dark:text-blue-200'>
              <strong>API keys are managed by your platform.</strong> No setup required — all calls use platform-level API credentials. Your platform credits are still used for each call.
            </AlertDescription>
          </Alert>
        )}

        {hasVideo && (
          <div className='mb-6'>
            <TrainingVideoModal
              trigger={
                <Button variant='outline' size='sm'>
                  <PlayCircle className='h-4 w-4 mr-2' />
                  {displayTitle || 'View Training'}
                </Button>
              }
              locationKey='api-settings'
            />
          </div>
        )}

        <Tabs
          value={activeTab}
          onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })}
          className='space-y-6'
        >
          <TabsList className='grid w-full grid-cols-3'>
            <TabsTrigger
              value='ai-providers'
              className='flex items-center gap-2'
            >
              <Brain className='h-4 w-4' />
              AI Providers
            </TabsTrigger>
            <TabsTrigger
              value='communication'
              className='flex items-center gap-2'
            >
              <Phone className='h-4 w-4' />
              Communication
            </TabsTrigger>
            <TabsTrigger value='automation' className='flex items-center gap-2'>
              <Zap className='h-4 w-4' />
              Automation
            </TabsTrigger>
          </TabsList>

          <TabsContent value='ai-providers' className='space-y-4'>
            <div className='grid gap-4 md:grid-cols-2'>
              <ApiKeyCard
                title='OpenAI'
                description='GPT models for script generation and conversation intelligence.'
                icon={Brain}
                required={true}
                serviceKey='openai'
                docUrl='https://platform.openai.com/api-keys'
                fields={[
                  {
                    key: 'openaiApiKey',
                    label: 'API Key',
                    placeholder: 'sk-...',
                    type: 'password',
                  },
                ]}
                validationStatus={validationStatus['openai']}
                formData={formData}
                handleInputChange={handleInputChange}
                handleSaveService={handleSaveService}
                showSecrets={showSecrets}
                toggleSecretVisibility={toggleSecretVisibility}
              />

              <ApiKeyCard
                title='xAI (Grok)'
                description='Alternative AI provider for diverse conversation styles.'
                icon={Brain}
                serviceKey='xai'
                docUrl='https://console.x.ai/'
                fields={[
                  {
                    key: 'xaiApiKey',
                    label: 'API Key',
                    placeholder: 'xai-...',
                    type: 'password',
                  },
                ]}
                validationStatus={validationStatus['xai']}
                formData={formData}
                handleInputChange={handleInputChange}
                handleSaveService={handleSaveService}
                showSecrets={showSecrets}
                toggleSecretVisibility={toggleSecretVisibility}
              />
            </div>
          </TabsContent>

          <TabsContent value='communication' className='space-y-4'>
            <div className='grid gap-4 md:grid-cols-2'>
              <ApiKeyCard
                title='Twilio'
                description='Voice calling and SMS capabilities for campaigns.'
                icon={Phone}
                required={true}
                serviceKey='twilio'
                docUrl='https://console.twilio.com/'
                fields={[
                  {
                    key: 'twilioSid',
                    label: 'Account SID',
                    placeholder: 'AC...',
                    validation: 'Found in your Twilio Console',
                  },
                  {
                    key: 'twilioAuthToken',
                    label: 'Auth Token',
                    placeholder: 'Your auth token',
                    type: 'password',
                    validation: 'Keep this secure',
                  },
                ]}
                validationStatus={validationStatus['twilio']}
                formData={formData}
                handleInputChange={handleInputChange}
                handleSaveService={handleSaveService}
                showSecrets={showSecrets}
                toggleSecretVisibility={toggleSecretVisibility}
              />

              <ApiKeyCard
                title='ElevenLabs'
                description='High-quality AI voice synthesis for natural conversations.'
                icon={Mic}
                required={true}
                serviceKey='elevenlabs'
                docUrl='https://elevenlabs.io/docs/api-reference/getting-started'
                fields={[
                  {
                    key: 'elevenlabsApiKey',
                    label: 'API Key',
                    placeholder: 'Your ElevenLabs API key',
                    type: 'password',
                  },
                ]}
                validationStatus={validationStatus['elevenlabs']}
                formData={formData}
                handleInputChange={handleInputChange}
                handleSaveService={handleSaveService}
                showSecrets={showSecrets}
                toggleSecretVisibility={toggleSecretVisibility}
              />

              <Card>
                <CardHeader className='pb-3'>
                  <div className='flex items-center gap-2'>
                    <Mic className='h-5 w-5 text-primary' />
                    <CardTitle className='text-lg'>Vapi</CardTitle>
                  </div>
                  <CardDescription>
                    Voice AI platform for automated phone conversations.
                    <a
                      href='https://docs.vapi.ai/'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-1 ml-2 text-primary hover:underline'
                    >
                      Documentation <ExternalLink className='h-3 w-3' />
                    </a>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <VapiAccountManager
                    apiKeys={authApiKeys || {}}
                    onRefresh={refreshApiKeys}
                    user={user}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value='automation' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>Live Lead Intake (Webhooks)</CardTitle>
                <CardDescription>
                  Turn any campaign into an always-on campaign: when a new lead comes in (Meta/Facebook
                  Lead Ads via Zapier or Make, Google Lead Forms, a website form, your CRM, or anything
                  that can send a web request), the lead is added to the campaign and called automatically.
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-6 text-sm'>
                {/* 1. Enable */}
                <div>
                  <h4 className='font-semibold mb-1'>1. Enable Live Lead Intake on a campaign</h4>
                  <ol className='list-decimal list-inside space-y-1 text-muted-foreground'>
                    <li>Open the campaign and click <span className='font-medium'>Edit</span>.</li>
                    <li>Turn on <span className='font-medium'>Live Lead Intake</span>.</li>
                    <li>Check the <span className='font-medium'>consent attestation</span> (it saves automatically).</li>
                    <li>Click <span className='font-medium'>Generate Key</span> &mdash; <span className='font-medium'>copy it now</span>, it&rsquo;s shown only once.</li>
                    <li>Copy the <span className='font-medium'>Webhook URL</span> from the same panel.</li>
                  </ol>
                </div>

                {/* 2. Send a lead */}
                <div>
                  <h4 className='font-semibold mb-1'>2. Send a lead</h4>
                  <p className='text-muted-foreground mb-2'>
                    POST to the Webhook URL with the API key in the <code>Authorization</code> header
                    and the lead as JSON:
                  </p>
                  <pre className='overflow-x-auto rounded-md bg-muted p-3 text-xs'>{`curl -X POST "<WEBHOOK_URL>" \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone": "+15551234567",
    "name": "Jane Doe",
    "email": "jane@example.com",
    "consent": true,
    "consent_text": "I agree to be contacted by an automated/AI system.",
    "source": "website"
  }'`}</pre>
                </div>

                {/* Request fields */}
                <div>
                  <h4 className='font-semibold mb-1'>Request fields</h4>
                  <ul className='list-disc list-inside space-y-1 text-muted-foreground'>
                    <li><code>phone</code> &mdash; <span className='font-medium'>required</span>. Also <code>phone_number</code>, <code>phoneNumber</code>, <code>mobile</code>. Normalized to E.164.</li>
                    <li><code>consent</code> &mdash; <span className='font-medium'>required</span>; must be truthy (<code>true</code>/<code>yes</code>/<code>1</code>). Rejected without it.</li>
                    <li><code>name</code> &mdash; optional. Also <code>first_name</code> + <code>last_name</code>, or <code>full_name</code>.</li>
                    <li><code>email</code> &mdash; optional (also <code>email_address</code>).</li>
                    <li><code>industry</code> &mdash; optional (also <code>vertical</code>).</li>
                    <li><code>consent_text</code>, <code>consent_timestamp</code> &mdash; recommended for your records.</li>
                    <li><code>source</code> &mdash; optional, e.g. <code>meta_lead_ad</code>, <code>website</code> (shows in Recent Leads).</li>
                  </ul>
                </div>

                {/* Response statuses */}
                <div>
                  <h4 className='font-semibold mb-1'>Response statuses</h4>
                  <p className='text-muted-foreground'>
                    <code>call_initiated</code> · <code>accepted</code> (saved, call-immediately off) ·{' '}
                    <code>queued_after_hours</code> (saved, outside working hours) · <code>rejected_dupe</code> ·{' '}
                    <code>rejected_no_consent</code> · <code>rejected_invalid</code> · <code>call_failed</code> ·{' '}
                    <code>test_skipped</code>
                  </p>
                </div>

                {/* 3. Meta via Zapier */}
                <div>
                  <h4 className='font-semibold mb-1'>3. Connect Meta Lead Ads (via Zapier)</h4>
                  <ol className='list-decimal list-inside space-y-1 text-muted-foreground'>
                    <li>In Zapier, trigger on <span className='font-medium'>Facebook Lead Ads &rarr; New Lead</span> and pick your form.</li>
                    <li>Add action <span className='font-medium'>Webhooks by Zapier &rarr; POST</span>: URL = your Webhook URL; Payload Type = JSON.</li>
                    <li>Map <code>phone</code>/<code>name</code>/<code>email</code> from the lead; set <code>consent</code> to <code>true</code> and <code>source</code> to <code>meta_lead_ad</code>.</li>
                    <li>Add header <code>Authorization</code> = <code>Bearer &lt;YOUR_API_KEY&gt;</code>.</li>
                    <li>Click <span className='font-medium'>Test step</span> &mdash; this fires the real request to your webhook (no form or live ad needed). The lead appears in Recent Leads. Then turn the Zap on.</li>
                  </ol>
                  <p className='text-muted-foreground mt-1'>
                    Make.com works the same way (Facebook Lead Ads trigger &rarr; HTTP request module &rarr;
                    <span className='font-medium'> Run once</span> to test).
                  </p>
                </div>

                {/* 4. Web form */}
                <div>
                  <h4 className='font-semibold mb-1'>4. Connect a website form</h4>
                  <p className='text-muted-foreground mb-2'>Drop this on any page (replace the URL and key):</p>
                  <pre className='overflow-x-auto rounded-md bg-muted p-3 text-xs'>{`<form id="lead-form">
  <input name="name" placeholder="Name" />
  <input name="phone" placeholder="Phone" required />
  <label><input type="checkbox" name="consent" required /> I agree to be
    contacted by an automated/AI system.</label>
  <button type="submit">Request a call</button>
</form>
<script>
document.getElementById('lead-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const f = e.target;
  await fetch('<WEBHOOK_URL>', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer <YOUR_API_KEY>',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: f.name.value, phone: f.phone.value,
      consent: f.consent.checked,
      consent_text: 'Website form consent checkbox',
      source: 'website'
    })
  });
  alert('Thanks! We will call you shortly.');
});
</script>`}</pre>
                </div>

                {/* 5. Test */}
                <div>
                  <h4 className='font-semibold mb-1'>5. Test before you go live</h4>
                  <ul className='list-disc list-inside space-y-1 text-muted-foreground'>
                    <li><span className='font-medium'>In-app (easiest):</span> in the campaign&rsquo;s Live settings, use <span className='font-medium'>Send a Test Lead</span> &mdash; &ldquo;add to list only&rdquo; (no call) or place a real test call to hear the campaign.</li>
                    <li><span className='font-medium'>From Zapier/Make:</span> use the built-in Test step / Run once (no web form needed).</li>
                    <li><span className='font-medium'>From your web form:</span> submit it once.</li>
                  </ul>
                  <p className='text-muted-foreground mt-1'>
                    Every test appears under <span className='font-medium'>Recent Leads</span>. The in-app
                    tester never adds the contact to the list (and a real test call is flagged so it&rsquo;s
                    excluded from stats). Leads sent from Zapier/Make/a web form during testing come in as
                    real leads &mdash; delete those from the campaign&rsquo;s list afterward.
                  </p>
                </div>

                {/* 6. Behavior */}
                <div>
                  <h4 className='font-semibold mb-1'>6. Behavior &amp; limits</h4>
                  <ul className='list-disc list-inside space-y-1 text-muted-foreground'>
                    <li>Duplicates are skipped (<code>rejected_dupe</code>).</li>
                    <li>Leads outside working hours are saved but not called until hours resume.</li>
                    <li>&ldquo;Call immediately&rdquo; off &rarr; leads are added but not auto-called.</li>
                    <li>Revoke a key anytime. Withdrawing consent revokes <span className='font-medium'>all</span> keys and disables live intake.</li>
                  </ul>
                </div>

                {/* TCPA */}
                <Alert>
                  <AlertDescription className='text-xs'>
                    <span className='font-semibold'>TCPA compliance:</span> AI/automated and prerecorded
                    marketing calls to mobile numbers require prior express written consent from the person
                    being called, captured on your form, before the call. Show clear consent language naming
                    your business and the use of AI/automated calling, and send it through the
                    <code> consent</code> (and ideally <code>consent_text</code>/<code>consent_timestamp</code>)
                    fields. Leads without consent are rejected. You attest to this when enabling Live Lead
                    Intake. This is not legal advice &mdash; consult counsel for your use case.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Missing Keys Alert */}
        {authApiKeys && !authApiKeys.openai && !authApiKeys.grok && (
          <Alert className='mt-6'>
            <AlertTriangle className='h-4 w-4' />
            <AlertDescription>
              <strong>AI Provider Required:</strong> You need to configure at
              least one AI provider (OpenAI or xAI) to create scripts and run
              campaigns.
            </AlertDescription>
          </Alert>
        )}

        <Alert className='mt-4'>
          <AlertDescription>
            Unsaved form entries are temporarily stored in your browser's
            session storage to persist across tab switches or reloads. This
            clears automatically when you close the tab. For security, avoid
            using shared devices and clear your browser data after saving
            sensitive keys.
          </AlertDescription>
        </Alert>
      </div>
    </TooltipProvider>
  );
};

export default ApiSettings;
